import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import GradientMesh from "@/components/GradientMesh";
import ComingSoonBadge from "@/components/ComingSoonBadge";
import { COMING_SOON_BY_PLAN } from "@/lib/comingSoon";
import {
  Sparkles,
  CalendarDays,
  Wand2,
  Move,
  Check,
  ArrowRight,
  Gem,
  BookOpen,
  ListChecks,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Marketing page: /social-media-scheduler
// ---------------------------------------------------------------------------
// SEO pillar page targeting the query "social media scheduler". All
// factual claims are traceable to code per the Batch 1 verified
// feature matrix. The unit tests in
// src/__tests__/marketing_batch1.test.js lock the critical sentences so
// accidental copy drift breaks the build.
// ---------------------------------------------------------------------------

const STEPS = [
  {
    n: "1",
    title: "Get a draft",
    body: "Either accept an Autopilot draft that Postmoro has already made for the week, or open Generate and produce a one-off post in a few seconds. Both flows give you a caption, hashtags, and an image in one go. Both are editable end-to-end.",
  },
  {
    n: "2",
    title: "Pick a date and time",
    body: "Every draft opens a small scheduling pane with the calendar. Choose the day, choose the time, choose which of your connected networks it should publish to.",
  },
  {
    n: "3",
    title: "Confirm",
    body: "The post lands on the calendar. You can open it again any time before it goes live to make changes.",
  },
];

const NETWORK_ROWS = [
  {
    name: "Facebook",
    body:
      "Connect a Facebook Page — or multiple Pages if you manage more than one. Postmoro lists all of them and lets you pick which one to post from, and publishes directly to your Facebook Page. Drafts, calendar planning, Brand Learning, and live publishing all work for your Page today.",
  },
  {
    name: "Instagram",
    body:
      "Connect an Instagram professional account (Business or Creator) linked to your Facebook Page. Postmoro drafts single-image feed posts — the caption, the hashtags, and the image, ready to review — and publishes them live to Instagram. Reels and Stories aren't part of the composer today.",
  },
  {
    name: "LinkedIn",
    body:
      "Publishing to your LinkedIn personal profile is live today. LinkedIn may periodically require you to reconnect your account. Postmoro shows an advance warning when your LinkedIn connection is nearing expiration and provides a direct reconnect option. Company Page posting isn't part of Postmoro yet.",
  },
  {
    name: "X",
    body:
      "Publishing to X is live today. Postmoro posts single updates — text on its own or text with one image. Threads and video aren't in the composer today.",
  },
];

const PLANS = [
  { id: "free", name: "Free Trial", price: "$0", posts: "5 published posts", platforms: "All 4 social platforms" },
  { id: "starter", name: "Starter", price: "$19.99", posts: "25 published posts", platforms: "All 4 social platforms" },
  { id: "pro", name: "Pro", price: "$79.99", posts: "60 published posts", platforms: "All four networks", highlight: true },
  { id: "business", name: "Business", price: "$249.99", posts: "Unlimited published posts (fair-use)", platforms: "All four networks", team: "Teams — 3 team seats included" },
];

const FAQS = [
  {
    q: "Can I schedule the same post to multiple networks at once?",
    a: "Yes. You can select multiple connected networks when scheduling a post. Review the final text and media for each destination before publishing.",
  },
  {
    q: "Can I edit a scheduled post before it goes live?",
    a: "Yes. Any scheduled post is editable up until publish time. Open the post from the calendar, change what you want, save. If you want to cancel it entirely, delete it from the calendar.",
  },
  {
    q: "Can I move a scheduled post to a different day?",
    a: "Yes. On the Pro and Business plans you can drag a post to a different day directly on the calendar. On all plans you can open a post and change its date and time from the scheduling pane.",
  },
  {
    q: "What happens if a network rejects my post?",
    a: "Postmoro surfaces the reason inside the app so you can fix the post — usually the fix is trimming the text, swapping the image, or reconnecting the account. Nothing else on your calendar is affected.",
  },
];

function MarketingHeader() {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur bg-[color:rgba(11,13,16,0.72)] border-b border-[color:var(--border-subtle)]"
      data-testid="marketing-header"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 h-14 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="marketing-logo">
          <div className="h-7 w-7 rounded-md bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] flex items-center justify-center shadow-[0_4px_18px_rgba(27,168,159,0.45)] shrink-0">
            <Gem className="h-4 w-4" />
          </div>
          <span className="font-display text-lg font-semibold tracking-tight truncate">
            Postmoro
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 text-sm text-[color:var(--text-2)]">
          <Link
            to="/ai-social-media-management"
            className="hover:text-[color:var(--text-1)]"
            data-testid="marketing-nav-ai"
          >
            AI social media management
          </Link>
          <Link
            to="/"
            className="hover:text-[color:var(--text-1)]"
            data-testid="marketing-nav-home"
          >
            Home
          </Link>
        </nav>
        <div className="flex items-center gap-2 shrink-0">
          <Link to="/login">
            <Button
              variant="ghost"
              className="text-[color:var(--text-2)] hover:text-[color:var(--text-1)] hidden sm:inline-flex"
              data-testid="marketing-signin"
            >
              Sign in
            </Button>
          </Link>
          <Link to="/login">
            <Button
              className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
              data-testid="marketing-start-free-header"
            >
              Start free
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function MarketingFooter() {
  return (
    <footer
      className="mt-24 border-t border-[color:var(--border-subtle)] py-10 text-sm text-[color:var(--text-3)]"
      data-testid="marketing-footer"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="h-5 w-5 rounded bg-gradient-to-br from-[color:var(--brand-primary)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] flex items-center justify-center">
            <Gem className="h-3 w-3" />
          </div>
          <span>© {new Date().getFullYear()} Postmoro LLC</span>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/privacy" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-privacy">
            Privacy
          </Link>
          <Link to="/terms" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-terms">
            Terms
          </Link>
          <Link to="/data-deletion" className="hover:text-[color:var(--text-1)]" data-testid="marketing-footer-data-deletion">
            Data deletion
          </Link>
        </div>
      </div>
    </footer>
  );
}

export default function SocialMediaSchedulerPage() {
  return (
    <>
      <SEO
        title="Social Media Scheduler for Facebook, Instagram, LinkedIn & X | Postmoro"
        description="Plan and schedule posts across Facebook, Instagram, LinkedIn, and X from one calendar. Postmoro drafts each post with AI. No credit card required."
        path="/social-media-scheduler"
        image="https://postmoro.com/marketing/calendar.png"
      />
      <div
        className="min-h-screen bg-[color:var(--bg-0)] text-[color:var(--text-1)]"
        data-testid="social-media-scheduler-page"
      >
        <MarketingHeader />

        {/* Hero */}
        <section className="relative overflow-hidden">
          <GradientMesh />
          <div className="absolute inset-0 hero-glow-strong pointer-events-none" />
          <div className="noise-overlay" aria-hidden="true" />
          <div className="relative max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 lg:pt-24 pb-10 sm:pb-14">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]" data-testid="hero-eyebrow">
                <CalendarDays className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
                Social media scheduler for small businesses
              </div>
              <h1
                className="font-display mt-5 text-[34px] sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05]"
                data-testid="hero-title"
              >
                One calendar for{" "}
                <span className="gradient-text">four networks</span>
              </h1>
              <p
                className="mt-5 text-base sm:text-lg text-[color:var(--text-2)] max-w-2xl"
                data-testid="hero-subtitle"
              >
                Plan and schedule posts across Facebook, Instagram, LinkedIn,
                and X from one calendar. Postmoro drafts each post with AI, so
                you can spend your time reviewing — not writing from scratch.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link to="/login">
                  <Button
                    size="lg"
                    className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold shadow-[0_18px_50px_-12px_rgba(27,168,159,0.55)]"
                    data-testid="hero-cta-primary"
                  >
                    Start free
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <Link
                  to="/ai-social-media-management"
                  className="text-sm text-[color:var(--text-2)] hover:text-[color:var(--text-1)] inline-flex items-center gap-1"
                  data-testid="hero-cta-secondary"
                >
                  Learn about the AI →
                </Link>
              </div>
              <ul
                className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-xs text-[color:var(--text-3)] max-w-xl"
                data-testid="hero-checks"
              >
                <li className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                  Free Trial plan, no credit card required
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                  Drag-and-drop rescheduling on Pro and Business
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                  Review and edit posts before publishing
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0" />
                  AI drafts on every plan
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Intro */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14 sm:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <p className="eyebrow text-[color:var(--brand-primary)]">
                The idea
              </p>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                The tool that keeps your posting week in one place
              </h2>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed">
                Most weeks, the problem isn&apos;t ideas — it&apos;s the ten-tab shuffle.
                Draft in a note, upload the image somewhere else, cross-post to
                another network, forget which day you promised to post on
                LinkedIn. Postmoro is a social media scheduler that keeps the
                whole week in one calendar, with drafts your{" "}
                <Link
                  to="/ai-social-media-management"
                  className="underline underline-offset-2 hover:text-[color:var(--text-1)]"
                  data-testid="body-link-ai"
                >
                  AI has already started for you
                </Link>
                .
              </p>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed">
                Open Postmoro, look at the week, edit anything that needs
                editing, and everything you scheduled lines up ready to publish
                on the days and times you picked. Nothing lives in a
                spreadsheet.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div
                className="relative rounded-2xl overflow-hidden border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] shadow-[0_25px_60px_-20px_rgba(0,0,0,0.6)]"
                data-testid="hero-product-image"
              >
                <img
                  src="/marketing/calendar.png"
                  alt="Screenshot of the Postmoro calendar with scheduled posts"
                  className="w-full h-auto object-cover object-left-top block"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">Workflow</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            From idea to scheduled, in three plain steps
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {STEPS.map((s) => (
              <div
                key={s.n}
                className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5"
                data-testid={`step-${s.n}`}
              >
                <div className="h-8 w-8 rounded-md bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] font-mono-tabular font-semibold flex items-center justify-center">
                  {s.n}
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-[color:var(--text-2)] leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5 flex flex-col sm:flex-row items-start gap-4">
            <div className="h-10 w-10 rounded-md bg-[color:rgba(27,168,159,0.10)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
              <Move className="h-5 w-5" />
            </div>
            <p className="text-sm text-[color:var(--text-2)] leading-relaxed">
              If the week needs rearranging, drag any scheduled post to a
              different day directly on the calendar. Drag-and-drop
              rescheduling is included on the Pro and Business plans.
            </p>
          </div>
        </section>

        {/* Approval vs auto */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <p className="eyebrow text-[color:var(--brand-primary)]">Modes</p>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                Approval-first, or fully automatic — you decide
              </h2>
              <p className="mt-4 text-[color:var(--text-2)] leading-relaxed">
                Postmoro is built to be honest about who&apos;s driving. There are
                two modes and one switch.
              </p>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5">
                <p className="text-sm text-[color:var(--text-2)] leading-relaxed">
                  By default, Autopilot drafts your week and holds each post
                  in an approval queue for you to review. Nothing publishes
                  without your say-so. Prefer fully hands-off? Switch off
                  approvals in Autopilot settings and Postmoro will publish
                  on the schedule you set without asking each time. Flip it
                  back whenever. Manual one-off posts (from Generate) always
                  let you choose to schedule or publish immediately.
                </p>
                <p className="mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">
                  Both modes use the same calendar. Both modes let you edit
                  any post before it goes live.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What happens when a post goes live */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">Publish</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            What happens when a post goes live
          </h2>
          <div className="mt-6 rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6">
            <p className="text-[color:var(--text-2)] leading-relaxed">
              At the scheduled time, Postmoro publishes the post to the network
              you picked. You&apos;ll see the post appear on the calendar as
              published, and you&apos;ll receive a confirmation email letting you
              know the post went out. If something goes wrong on the network&apos;s
              side, Postmoro tells you inside the app so you can fix it and
              reschedule.
            </p>
            <p className="mt-4 text-[color:var(--text-2)] leading-relaxed">
              Connections need care, and Postmoro doesn&apos;t hide that from you.
              LinkedIn may periodically require you to reconnect your account.
              Postmoro shows an advance warning when your LinkedIn connection
              is nearing expiration and provides a direct reconnect option.
            </p>
          </div>
        </section>

        {/* Networks */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">Networks</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            What each network looks like in Postmoro
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {NETWORK_ROWS.map((n) => (
              <div
                key={n.name}
                className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6"
                data-testid={`network-card-${n.name.toLowerCase()}`}
              >
                <h3 className="font-display text-lg font-semibold">{n.name}</h3>
                <p className="mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">
                  {n.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Plans compact table */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <p className="eyebrow text-[color:var(--brand-primary)]">Plans</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            Plans that fit how much you post
          </h2>
          <div className="mt-8 rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[color:var(--border-subtle)]">
              {PLANS.map((p) => (
                <div
                  key={p.id}
                  className={`p-5 ${
                    p.highlight ? "bg-[color:rgba(27,168,159,0.06)]" : ""
                  }`}
                  data-testid={`plan-row-${p.id}`}
                >
                  <div className="flex items-baseline justify-between">
                    <div className="font-display text-base font-semibold">
                      {p.name}
                    </div>
                    {p.highlight && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[color:var(--brand-primary)] text-[color:var(--bg-0)] font-semibold">
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="mt-1 font-display text-xl font-semibold">
                    {p.price}
                    <span className="text-xs font-normal text-[color:var(--text-3)] ml-1">
                      / month
                    </span>
                  </div>
                  <ul className="mt-3 space-y-1.5 text-xs text-[color:var(--text-2)]">
                    <li className="inline-flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0 mt-0.5" />
                      <span>{p.posts}</span>
                    </li>
                    <li className="inline-flex items-start gap-2">
                      <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0 mt-0.5" />
                      <span>{p.platforms}</span>
                    </li>
                    {p.team && (
                      <li className="inline-flex items-start gap-2" data-testid={`plan-team-row-${p.id}`}>
                        <Check className="h-3.5 w-3.5 text-[color:var(--success)] shrink-0 mt-0.5" />
                        <span>{p.team}</span>
                      </li>
                    )}
                    {(COMING_SOON_BY_PLAN[p.id] || []).length > 0 && (
                      <>
                        {(COMING_SOON_BY_PLAN[p.id] || []).map((f, i) => (
                          <li key={f} className={"flex gap-2 items-start opacity-90" + (i === 0 ? " pt-3 mt-2 border-t border-[color:var(--border-subtle)]" : "")} data-testid={`plan-coming-soon-row-${p.id}`}>
                            <Sparkles className="h-3.5 w-3.5 text-[color:rgb(255,190,110)] shrink-0 mt-0.5" aria-hidden="true" />
                            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                              <span>{f.replace(/\s*—\s*Coming Soon\s*$/i, "")}</span>
                              <ComingSoonBadge />
                            </span>
                          </li>
                        ))}
                      </>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <p className="mt-4 text-xs text-[color:var(--text-3)]">
            Start on Free Trial, upgrade whenever you&apos;re ready. No credit card
            required.
          </p>
        </section>

        {/* FAQ */}
        <section
          className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14"
          data-testid="faq-section"
        >
          <p className="eyebrow text-[color:var(--brand-primary)]">FAQ</p>
          <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
            Frequently asked questions
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            {FAQS.map((item, idx) => (
              <div
                key={item.q}
                className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-5"
                data-testid={`faq-item-${idx}`}
              >
                <div className="inline-flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-[color:var(--brand-primary)]" />
                  <h3 className="font-display text-base font-semibold">
                    {item.q}
                  </h3>
                </div>
                <p className="mt-3 text-sm text-[color:var(--text-2)] leading-relaxed">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Closing CTA */}
        <section className="max-w-[1180px] mx-auto px-4 sm:px-6 lg:px-10 py-14">
          <div className="rounded-2xl border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-8 sm:p-10 text-center">
            <div className="inline-flex items-center gap-2 rounded-full glass px-3 py-1 text-xs text-[color:var(--text-2)]">
              <ListChecks className="h-3.5 w-3.5 text-[color:var(--brand-primary)]" />
              Free Trial, no credit card required
            </div>
            <h2 className="mt-3 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Try Postmoro&apos;s scheduler for free
            </h2>
            <p className="mt-3 text-[color:var(--text-2)] max-w-xl mx-auto">
              Connect a network, look at the calendar, generate a first draft,
              and see what a whole week looks like when Postmoro has already
              drafted it.
            </p>
            <div className="mt-6">
              <Link to="/login">
                <Button
                  size="lg"
                  className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                  data-testid="closing-cta"
                >
                  Start free
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <MarketingFooter />
      </div>
    </>
  );
}
